import * as React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { TaskRecord } from "@nexus/contracts";

import { shouldSuppressSetGenerationToast } from "@/features/task-progress/lib/should-suppress-set-generation-toast";
import {
  buildPersonaSetStatusMap,
  detectPersonaSetGenerationTransitions,
  type PersonaSetStatus,
} from "../lib/persona-opportunity-set-generation-state";
import {
  detectFinishedPersonaSetGenerationIds,
  PERSONA_SET_GENERATION_OUTCOME,
  readPersonaSetGenerationOutcome,
} from "../lib/persona-set-generation-task";

const COMPLETION_TOAST_MS = 8_000;

interface PersonaSetForToasts {
  id: string;
  name: string;
  status: PersonaSetStatus;
}

export interface UsePersonaSetGenerationToastsOptions {
  /** Server-driven list. Transitions are detected between renders of this array. */
  sets: readonly PersonaSetForToasts[];
  /** Active task-progress records — used to avoid duplicate completion toasts. */
  activeTasks?: readonly TaskRecord[];
  /** Reset transition tracking when the active tenant changes. */
  tenantId?: string | null;
  /** Called when the user clicks "View" on the completion toast. */
  onOpenSet: (setId: string) => void;
  /** Called when the user clicks "Retry" on the failure toast. */
  onRetrySet?: (setId: string) => void;
}

/**
 * One toast per GENERATING → READY / GENERATING → FAILED transition seen in the
 * polled list, mirroring `useGenerationTransitionEffects` on the legacy page.
 *
 * Neither toast navigates on its own — a set finishing while the user is doing
 * something else must not yank them into the workspace.
 */
export function usePersonaSetGenerationToasts({
  sets,
  activeTasks = [],
  tenantId,
  onOpenSet,
  onRetrySet,
}: UsePersonaSetGenerationToastsOptions): void {
  const { t } = useTranslation();
  const previousStatusRef = React.useRef<Map<string, PersonaSetStatus>>(new Map());
  const inProgressTaskIdsRef = React.useRef<Set<string>>(new Set());
  const trackedTenantRef = React.useRef(tenantId);

  // Stable refs so the effect below only re-runs when the list itself changes.
  const onOpenSetRef = React.useRef(onOpenSet);
  const onRetrySetRef = React.useRef(onRetrySet);
  onOpenSetRef.current = onOpenSet;
  onRetrySetRef.current = onRetrySet;

  React.useEffect(() => {
    if (trackedTenantRef.current !== tenantId) {
      trackedTenantRef.current = tenantId;
      previousStatusRef.current = new Map();
      inProgressTaskIdsRef.current = new Set();
    }

    const transitions = detectPersonaSetGenerationTransitions(previousStatusRef.current, sets);
    previousStatusRef.current = buildPersonaSetStatusMap(sets);

    for (const transition of transitions) {
      if (shouldSuppressSetGenerationToast(activeTasks, transition.setId)) {
        continue;
      }

      if (transition.to === "READY") {
        toast.success(
          t("personaOpportunities.toast.setGenerated", "Persona opportunity set is ready"),
          {
            description: transition.name,
            duration: COMPLETION_TOAST_MS,
            action: {
              label: t("personaOpportunities.toast.viewSet", "View"),
              onClick: () => onOpenSetRef.current(transition.setId),
            },
          },
        );
        continue;
      }

      if (transition.to !== "FAILED") {
        continue;
      }

      toast.error(
        t("personaOpportunities.toast.setFailed", "Persona opportunity set failed to generate"),
        {
          description: transition.name,
          duration: COMPLETION_TOAST_MS,
          ...(onRetrySetRef.current
            ? {
                action: {
                  label: t("common.retry", "Retry"),
                  onClick: () => onRetrySetRef.current?.(transition.setId),
                },
              }
            : {}),
        },
      );
    }

    const { inProgress, finishedIds } = detectFinishedPersonaSetGenerationIds(
      inProgressTaskIdsRef.current,
      activeTasks,
    );
    inProgressTaskIdsRef.current = inProgress;

    for (const correlationId of finishedIds) {
      const task = activeTasks.find((entry) => entry.correlationId === correlationId);
      if (!task) continue;
      const outcome = readPersonaSetGenerationOutcome(task);
      if (outcome === PERSONA_SET_GENERATION_OUTCOME.NO_OPPORTUNITIES) {
        toast.warning(
          t("personaOpportunities.toast.noOpportunities", "No opportunities were found"),
          { description: task.title, duration: COMPLETION_TOAST_MS },
        );
        continue;
      }
      if (outcome === PERSONA_SET_GENERATION_OUTCOME.INDUSTRY_NOT_FOUND) {
        toast.error(
          t("personaOpportunities.toast.industryNotFound", "Industry not found"),
          { description: task.title, duration: COMPLETION_TOAST_MS },
        );
      }
    }
  }, [sets, activeTasks, tenantId, t]);
}
